import { useMemo } from 'react';

import { sha256 } from 'js-sha256';

import { getAuthState, getEmail } from '@/shared/authentication';

import { useAppSelector } from '@/hooks/redux-hooks';

import { getErrorMessage } from '@/utils/invariant';

import { Logger } from '../logger';

export const FEATURE_FLAG_NAMES = [
  'CoverageOverview',
  'LgmCattleEndorsementsAipSync',
  'CmePositions'
] as const;
export type FeatureFlagName = (typeof FEATURE_FLAG_NAMES)[number];

export interface FeatureFlag {
  enabled: boolean;
  users: string[];
}

type FeatureFlagsConfig = Record<string, FeatureFlag>;

function isFeatureFlagConfig(value: unknown): value is FeatureFlagsConfig {
  if (typeof value !== 'object' || value === null) return false;

  return Object.values(value).every(
    flag =>
      typeof flag === 'object' &&
      flag !== null &&
      'enabled' in flag &&
      typeof (flag as FeatureFlag).enabled === 'boolean' &&
      Array.isArray((flag as FeatureFlag).users)
  );
}

export function useFeatureFlags(): Record<FeatureFlagName, boolean> {
  const currentUserEmail = useAppSelector(getEmail)?.toLowerCase().trim();
  const authState = useAppSelector(getAuthState);

  return useMemo(() => {
    try {
      const parsed: unknown = JSON.parse(import.meta.env.VITE_FEATURE_FLAGS as string);

      if (!isFeatureFlagConfig(parsed)) {
        throw new Error('Invalid feature flags configuration');
      }

      const featureFlags = parsed;
      const hashedEmail = currentUserEmail ? sha256(currentUserEmail) : null;

      return new Proxy(featureFlags as unknown as Record<FeatureFlagName, boolean>, {
        get(_, prop: FeatureFlagName) {
          if (typeof prop !== 'string') return false;

          const flag = featureFlags[prop];
          if (!flag?.enabled) return false;
          if (authState === 'globalAdministrator') return true;
          if (!hashedEmail) return false;
          return flag.users.includes(hashedEmail);
        }
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to check feature flag.');
      Logger.trace(errorMessage, {
        level: 'Error',
        sentryLogging: true
      });

      return FEATURE_FLAG_NAMES.reduce(
        (acc, name) => ({ ...acc, [name]: false }),
        {} as Record<FeatureFlagName, boolean>
      );
    }
  }, [currentUserEmail, authState]);
}

// eslint-disable-next-line import/no-default-export
export default useFeatureFlags;
